import React from 'react';
import { Filter, X, Download, Search } from 'lucide-react';
import { MultiSelect } from '../ui';
import { ProfitAnalysisService } from '../../services/profitAnalysisService';

const ProfitFilters = ({
  filters,
  options,
  onChange,
  onClear,
  onExport,
  projectCount,
  totalCount
}) => {
  const hasActiveFilters =
    (filters.templateTypes && filters.templateTypes.length > 0) ||
    (filters.clientIds && filters.clientIds.length > 0) ||
    (filters.states && filters.states.length > 0) ||
    (filters.acreageRanges && filters.acreageRanges.length > 0) ||
    (filters.billingStatuses && filters.billingStatuses.length > 0) ||
    (filters.modelingStatuses && filters.modelingStatuses.length > 0 &&
      !(filters.modelingStatuses.length === 1 && filters.modelingStatuses[0] === 'Completed')) ||
    (filters.searchTerm && filters.searchTerm.trim());

  const handleDateChange = (field, value) => {
    onChange({ [field]: value || null });
  };

  const handleSearchChange = (e) => {
    onChange({ searchTerm: e.target.value });
  };

  // Convert client IDs to names for MultiSelect
  const selectedClientNames = (filters.clientIds || [])
    .map(id => options.clients.find(c => c.id === id)?.name)
    .filter(Boolean);

  const handleClientChange = (selectedNames) => {
    const selectedIds = selectedNames
      .map(name => options.clients.find(c => c.name === name)?.id)
      .filter(Boolean);
    onChange({ clientIds: selectedIds });
  };

  return (
    <div className="filters-section">
      <div className="filters-header">
        <div className="filters-title">
          <Filter size={16} />
          Filters
        </div>
        <div className="filters-actions">
          <span className="project-count">
            {projectCount === totalCount
              ? `${totalCount} projects`
              : `${projectCount} of ${totalCount} projects`
            }
          </span>
          {hasActiveFilters && (
            <button className="clear-filters-btn" onClick={onClear}>
              <X size={14} />
              Clear Filters
            </button>
          )}
          <button className="export-btn" onClick={onExport}>
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      <div className="filters-grid">
        {/* Project Search */}
        <div className="filter-group" style={{ gridColumn: 'span 2' }}>
          <label className="filter-label">Search Projects</label>
          <div style={{ position: 'relative' }}>
            <Search
              size={16}
              style={{
                position: 'absolute',
                left: '0.75rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: 'var(--text-tertiary)'
              }}
            />
            <input
              type="text"
              className="filter-input"
              placeholder="Job #, project name, or client..."
              value={filters.searchTerm || ''}
              onChange={handleSearchChange}
              style={{ paddingLeft: '2.25rem' }}
            />
          </div>
        </div>

        {/* Date Range */}
        <div className="filter-group">
          <label className="filter-label">Start Date</label>
          <input
            type="date"
            className="filter-input"
            value={filters.startDate || ''}
            onChange={(e) => handleDateChange('startDate', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label className="filter-label">End Date</label>
          <input
            type="date"
            className="filter-input"
            value={filters.endDate || ''}
            onChange={(e) => handleDateChange('endDate', e.target.value)}
          />
        </div>

        {/* Template Type */}
        <div className="filter-group">
          <MultiSelect
            label="Template Type"
            options={options.templateTypes || []}
            value={filters.templateTypes || []}
            onChange={(selected) => onChange({ templateTypes: selected })}
            placeholder="All types"
            size="md"
          />
        </div>

        {/* Client */}
        <div className="filter-group">
          <MultiSelect
            label="Client"
            options={(options.clients || []).map(c => c.name)}
            value={selectedClientNames}
            onChange={handleClientChange}
            placeholder="All clients"
            size="md"
          />
        </div>

        {/* State */}
        <div className="filter-group">
          <MultiSelect
            label="State"
            options={options.states || []}
            value={filters.states || []}
            onChange={(selected) => onChange({ states: selected })}
            placeholder="All states"
            size="md"
          />
        </div>

        {/* Acreage */}
        <div className="filter-group">
          <MultiSelect
            label="Acreage"
            options={ProfitAnalysisService.ACREAGE_RANGES.map(r => r.label)}
            value={filters.acreageRanges || []}
            onChange={(selected) => onChange({ acreageRanges: selected })}
            placeholder="All sizes"
            size="md"
          />
        </div>

        {/* Modeling Status */}
        <div className="filter-group">
          <MultiSelect
            label="Modeling Status"
            options={options.modelingStatuses || []}
            value={filters.modelingStatuses || []}
            onChange={(selected) => onChange({ modelingStatuses: selected })}
            placeholder="All statuses"
            size="md"
          />
        </div>

        {/* Billing Status */}
        <div className="filter-group">
          <MultiSelect
            label="Billing Status"
            options={options.billingStatuses || ProfitAnalysisService.BILLING_STATUSES}
            value={filters.billingStatuses || []}
            onChange={(selected) => onChange({ billingStatuses: selected })}
            placeholder="All statuses"
            size="md"
          />
        </div>
      </div>
    </div>
  );
};

export default ProfitFilters;
